import { useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, ImageOff, Loader2, Pause, Play, Send, Undo2 } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { useConfirm } from '@orisini/ui/components/confirm'
import { useAuth } from '@orisini/ui/context/AuthContext'
import { accountsApi, formatClock, mediaUrl, type QueueItem } from '../../accounts/lib/accounts'

/**
 * Daftar antrean, dikelompokkan per hari. Dipakai Publishing Queue (semua
 * akun) dan tab Overview akun (satu akun, `showAccount=false`).
 */
export function QueueList({
  items,
  onChanged,
  showAccount = true,
}: {
  items: QueueItem[]
  onChanged: () => void
  showAccount?: boolean
}) {
  const groups = new Map<string, QueueItem[]>()
  for (const it of items) {
    const key = it.scheduledAt
      ? new Date(it.scheduledAt).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })
      : 'Not scheduled yet'
    groups.set(key, [...(groups.get(key) ?? []), it])
  }

  return (
    <div className="space-y-6">
      {[...groups].map(([day, list]) => (
        <section key={day} className="space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{day}</h3>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {list.map((it) => (
              <QueueRow key={`${it.workspaceId}:${it.id}`} item={it} onChanged={onChanged} showAccount={showAccount} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

function QueueRow({ item: it, onChanged, showAccount }: { item: QueueItem; onChanged: () => void; showAccount: boolean }) {
  const { me } = useAuth()
  const confirm = useConfirm()
  const [busy, setBusy] = useState<string | null>(null)
  const thumb = mediaUrl(it.thumbUrl, it.productCode, me?.products)
  const judul = it.title?.trim() || (it.productCode === 'pinboss' ? 'Untitled pin' : 'No caption')

  const jalankan = async (action: 'post-now' | 'hold' | 'unhold' | 'dequeue', sukses: string) => {
    setBusy(action)
    try {
      await accountsApi.itemAction(it.workspaceId, it.id, action)
      toast.success(sukses)
      onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setBusy(null)
    }
  }

  const postNow = async () => {
    const ok = await confirm({
      title: 'Post this now?',
      description: `It goes out to ${it.workspaceName} on ${it.platform} as soon as Orisini Agent picks it up.`,
      confirmLabel: 'Post now',
    })
    if (ok) await jalankan('post-now', 'Sent to Orisini Agent')
  }

  const keluarkan = async () => {
    const ok = await confirm({
      title: 'Remove from the queue?',
      description: 'It goes back to drafts and will not be published. You can queue it again later.',
      confirmLabel: 'Remove',
      destructive: true,
    })
    if (ok) await jalankan('dequeue', 'Removed from the queue')
  }

  return (
    <li className="flex items-center gap-3 p-3">
      <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
        {thumb ? (
          <img src={thumb} alt="" className="size-full object-cover" loading="lazy" />
        ) : (
          <ImageOff className="size-4 text-muted-foreground" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{judul}</p>
        <p className="truncate text-xs text-muted-foreground">
          {[
            it.scheduledAt ? formatClock(it.scheduledAt) : null,
            showAccount ? `${it.workspaceName} · ${it.platform}` : it.platform,
            it.detail,
            it.fixedTime ? 'Fixed time' : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {(it.onHold || it.problems.length > 0 || it.lastError) && (
          <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-amber-400">
            <AlertTriangle className="size-3 shrink-0" />
            {it.onHold ? 'On hold — skipped until you resume it' : it.lastError || it.problems.join(', ')}
          </p>
        )}
      </div>

      <div className="flex shrink-0 gap-1">
        <Button variant="ghost" size="sm" disabled={!!busy} onClick={postNow} aria-label="Post now" title="Post now">
          {busy === 'post-now' ? <Loader2 className="animate-spin" /> : <Send />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!!busy}
          onClick={() => jalankan(it.onHold ? 'unhold' : 'hold', it.onHold ? 'Resumed' : 'On hold')}
          aria-label={it.onHold ? 'Resume' : 'Hold'}
          title={it.onHold ? 'Resume' : 'Hold'}
        >
          {busy === 'hold' || busy === 'unhold' ? <Loader2 className="animate-spin" /> : it.onHold ? <Play /> : <Pause />}
        </Button>
        <Button variant="ghost" size="sm" disabled={!!busy} onClick={keluarkan} aria-label="Remove from queue" title="Remove from queue">
          {busy === 'dequeue' ? <Loader2 className="animate-spin" /> : <Undo2 />}
        </Button>
      </div>
    </li>
  )
}
